/**
 * 지금 화면이 보여 주는 사건의 데이터.
 *
 * ## 왜 모듈 전역인가
 *
 * 서버는 방 여럿을 동시에 굴리니 데이터를 방이 들고 있어야 한다
 * (`shared/episode-rules.ts`). **브라우저는 그럴 필요가 없다** — 한 탭은 한 방만
 * 본다. 그래서 여기서는 전역 하나로 두고 `load()` 를 판 시작에 한 번 부른다.
 *
 * 이렇게 하면 `props.ts` `tape.ts` `overview.ts` `hud.ts` 가 예전처럼 그냥
 * 「지금 데이터」를 읽으면 된다. 그 넷에 에피소드 인자를 줄줄이 흘려보내는 것보다
 * 읽기 쉽다.
 *
 * **규칙 하나: `load()` 보다 먼저 읽지 말 것.** 읽으면 사흘이 나온다.
 * 판을 여는 경로(`main.ts` 의 `run`)가 제일 먼저 부른다.
 */

import type { PropDef, RoomDef } from '../../shared/level-defs';
import type { ObjState } from '../../shared/protocol';
import type { LevelDef } from '../../shared/level-types';
import { castOf, type CastSet } from '../../shared/cast-of';
import { episodeOr, useEpisodeLevel } from '../../shared/episode-registry';

export interface EpisodeData {
  id: string;
  PROPS: PropDef[];
  PROP_BY_ID: Record<string, PropDef>;
  ROOMS: RoomDef[];
  initialObjects(): Record<string, ObjState>;
  /** 좌표가 어느 방인지. 밖이면 null — 눈·조명 판정이 이걸 본다 */
  roomAt(x: number, z: number): RoomDef | null;
  /** 출입통제선처럼 그 사건에만 있는 연출 */
  tapes: boolean;
  /** 바깥에 눈이 오는가. 초치성은 음력 보름 가을밤이다 */
  snow: boolean;
  /**
   * 문이 어떻게 열리는가.
   *
   * `swing` 은 경첩을 축으로 도는 여닫이, `slide` 는 벽을 따라 미는 미닫이다.
   * 한옥에서 문이 바깥으로 벌컥 열리면 그 순간 한옥이 아니게 된다.
   */
  doorStyle: 'swing' | 'slide';
  /** 이 사건의 배역 명단. 누가 누구인지는 전원이 안다 */
  cast: CastSet;
  /**
   * **피해자 배역 id.** 바닥에 눕는 모델이 이걸로 정해진다.
   *
   * `props.ts` 가 `'han'`(사흘의 한서진)을 박아 두고 있었다. 그래서 초치성
   * 본당에는 강만신이 아니라 사흘 피해자가 누워 있었다 — 사건이 둘이 된
   * 뒤로 이런 상수는 전부 여기로 와야 한다.
   */
  victim: string;
  /** 서버 포즈 저장소(/api/poses) — 이름 → 관절 포즈. 부팅 때 한 번 받는다 */
  poses?: Record<string, import('./motion').Pose>;
  /** 시신 부위 조사 목록 */
  bodyParts: readonly { id: string; label: string; under?: boolean; clue?: string }[];
  /**
   * 눈높이(m). **맵이 아니라 사람을 조절하는 손잡이다.**
   *
   * 한옥은 방도 문도 낮다. 서양식 실내 기준인 1.62 로 서면 창호 상인방이
   * 눈 바로 앞에 오고 천장이 머리를 누른다 — 거인이 된 기분이 든다.
   *
   * 맵 치수를 건드리면 기물·단서 좌표가 전부 흔들리므로 사람 쪽을 낮춘다.
   *
   * 1.62 → 1.45 → **1.25** 로 두 번 내렸다. 실측 인체 치수를 고집할 자리가
   * 아니었다 — 이 집에서 필요한 것은 「맞는 키」가 아니라 **낮은 방에 들어와
   * 있다는 감각**이고, 그건 숫자가 아니라 눈으로 판정할 수밖에 없다.
   */
  eyeHeight: number;
  /**
   * 공간 정의. **발밑 바닥을 계산하는 데 쓴다.**
   *
   * 사흘은 `RoomDef` 체계라 층이 없다 — 바닥 하나짜리 `LevelDef` 를 주면
   * 예전처럼 평지로 동작한다.
   */
  level: LevelDef;
  /**
   * **「사흘」의 마을을 함께 지을 것인가.**
   *
   * 마을 외피(`buildFacades`)와 바깥 지형(눈 바닥·능선·나무·가로등)은
   * 사흘 전용인데 사건과 무관하게 늘 지어지고 있었다. 그래서 초치성 점집
   * 담장 너머에 폐교 마을이 서 있었다.
   */
  village: boolean;
}

let cur: EpisodeData | null = null;

/** 판을 열기 전에 한 번. 같은 사건을 다시 부르면 아무 일도 안 한다 */
export async function loadEpisodeData(id: string): Promise<EpisodeData> {
  if (cur?.id === id) return cur;
  /**
   * **배럴(`shared/level`)을 읽는다. `level-sahol` 을 직접 읽으면 안 된다.**
   *
   * 예전에는 여기서 v3 를 직접 불렀다. 그래서 배럴을 v7 로 돌려도 벽·건물만
   * v7 이고 **발밑은 v3 평지**였다 — 새로 낸 언덕 위 평지(3.6m)와 비탈이
   * 지형에 없어서 한서진 집에 올라갈 수가 없었고, 방 판정도 v3 이름이 나왔다.
   */
  // 배럴을 이 에피소드로 갈아 끼운 뒤 읽는다 — world·props·outside 가 전부 배럴을 본다
  const key = episodeOr(id);
  await useEpisodeLevel(key);
  const s = await import('../../shared/level');
  cur = {
    id: key,
    PROPS: s.PROPS,
    PROP_BY_ID: s.PROP_BY_ID,
    ROOMS: s.ROOMS,
    initialObjects: s.initialObjects,
    roomAt: s.roomAt,
    level: s.LEVEL,
    tapes: true,
    snow: true,
    doorStyle: 'swing',
    cast: castOf(key),
    victim: 'han',
    bodyParts: (s as { BODY_PARTS?: readonly { id: string; label: string; under?: boolean; clue?: string }[] }).BODY_PARTS ?? [],
    eyeHeight: 1.62,
    village: true,
  };
  return cur;
}

/**
 * 지금 데이터. `load()` 전에 부르면 사흘이 나온다 — 정적 import 로 돌던
 * 예전 동작 그대로라, 안 고친 자리가 있어도 화면이 비지는 않는다.
 */
export function ep(): EpisodeData {
  if (!cur) throw new Error('loadEpisodeData() 를 먼저 불러야 한다');
  return cur;
}

/** 아직 로드 전이면 undefined. 판 밖(카탈로그 등)에서 쓰는 화면용 */
export function epMaybe(): EpisodeData | null {
  return cur;
}
